'use client';

import { useEffect, useRef, type ElementType } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Home } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Crumb {
  /** Shown text — or give `navKey` (a `nav.*` key, e.g. 'events') instead. */
  label?: string;
  navKey?: string;
  /** Leave out on the last crumb — that is the page you are on. */
  href?: string;
  icon?: ElementType;
}

/**
 * The one breadcrumb trail (Gordon 2026-10-31: "complete bread crumbs
 * directory path … all should be links … a second way to go back to home",
 * then "only icon" for Home and "remove slash"). Starts at the Home icon;
 * every crumb carries an icon — the icons are the separators — and every
 * crumb but the current page is a link. Long trails scroll sideways on
 * phones, start scrolled to the end, and keep Home pinned.
 *
 * `heading` renders the current page as the page's <h1> (list pages, where
 * the trail IS the title). No back arrow — Home is the way back (Gordon
 * 2026-10-31: "remove arrow back before home icon … it's redundant").
 */
export function Breadcrumbs({
  items,
  heading = false,
  size = 'sm',
  className,
}: {
  items: Crumb[];
  heading?: boolean;
  size?: 'sm' | 'lg';
  className?: string;
}) {
  const tNav = useTranslations('nav');
  const tCommon = useTranslations('common');
  const listRef = useRef<HTMLOListElement>(null);

  const home: Crumb = { label: tNav('home'), href: '/home', icon: Home };
  const trail: Crumb[] = [home, ...items]
    .map(c => (c.label != null ? c : { ...c, label: c.navKey ? tNav(c.navKey) : '' }));
  const current = trail[trail.length - 1];

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [trail.length, current.label]);


  const iconClass = size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';

  return (
    <nav aria-label={tCommon('breadcrumb')} className={cn('flex min-w-0 items-center gap-1', className)}>
      <ol
        ref={listRef}
        className={cn(
          'hide-scrollbar flex min-w-0 items-center gap-3 overflow-x-auto whitespace-nowrap',
          size === 'lg' ? 'text-lg sm:text-xl' : 'text-sm',
        )}
      >
        {trail.map((c, i) => {
          const last = i === trail.length - 1;
          const Icon = c.icon;
          // Home is the icon alone (Gordon 2026-10-31: "only icon"; the word
          // stays for screen readers) and stays pinned while a long trail
          // scrolls under it, so the way home is always one tap away.
          const pinnedHome = i === 0;
          // Phones: the steps between Home and this page are their icon alone
          // (Gordon 2026-10-31: "Marketplace title is cropped? maybe just the
          // marketplace icon") — still links, named for screen readers/tooltip.
          const middle = !pinnedHome && !last && !!c.icon;
          const body = (
            <>
              {Icon && <Icon className={cn(iconClass, 'shrink-0 text-primary')} aria-hidden="true" />}
              <span className={cn('truncate', last ? 'max-w-[75vw] sm:max-w-[36rem]' : 'max-w-[16rem]', pinnedHome && 'sr-only', middle && 'max-sm:sr-only')}>{c.label}</span>
            </>
          );
          return (
            <li key={`${i}-${c.label}`} className={cn('flex shrink-0 items-center gap-1', pinnedHome && 'sticky left-0 z-[1] bg-background pr-1 after:pointer-events-none after:absolute after:left-full after:top-0 after:h-full after:w-5 after:bg-gradient-to-r after:from-background after:to-transparent')}>
              {last || !c.href ? (
                heading && last ? (
                  <h1 aria-current="page" className="inline-flex items-center gap-1.5 font-bold text-foreground [font-size:inherit] [line-height:inherit]">{body}</h1>
                ) : (
                  <span aria-current={last ? 'page' : undefined} className={cn('inline-flex items-center gap-1.5', last ? 'font-semibold text-foreground' : 'text-muted-foreground')}>{body}</span>
                )
              ) : (
                <Link href={c.href} title={c.label} className="inline-flex items-center gap-1.5 rounded px-0.5 text-muted-foreground transition-colors hover:text-foreground hover:underline">
                  {body}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** `/artists/<slug>` for any artist-ish value — the artists/[slug] route
 *  also resolves a slugified name, so a link always lands somewhere real. */
export function artistHref(a: any, fallbackName?: string): string | undefined {
  const key = (a && typeof a === 'object' && (a.slug || a.docId || a.artistId || a.id)) || '';
  if (key) return `/artists/${key}`;
  const name = (typeof a === 'string' ? a : a?.name) || fallbackName || '';
  const slug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  return slug ? `/artists/${slug}` : undefined;
}
